import type {ReactElement} from 'react';
import {useEffect} from 'react';
import {useRef} from 'react';
import React, {useState} from 'react';
import {Text, TextSize} from '@Components/text';
import {noop} from '@Utils/general.util';
import {Icon} from '@Components/icon-v2';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import styles from './color-palette.module.scss';
import type {ColorPaletteProps} from '@Components/color-palettes/color-palette/color-palette.types';
import {ColorPaletteSize, DEFAULT_COLORS} from '@Components/color-palettes/color-palette/color-palette.types';
import {motion} from 'framer-motion';
import {isMobile} from 'react-device-detect';
import {ClickableDiv} from '@Components/clickable-div';
import {isTouchDevice} from '@Utils/browser.util';

export function ColorPalette({
  size = ColorPaletteSize.MEDIUM,
  isSelected = false,
  isBeingApplied = false,
  showNameOverlayOnHover = false,
  showNameLabel = false,
  allowShuffle = false,
  showApplyingState = true,
  disableSelection = false,
  onClick = noop,
  onColorPaletteDoneApplying = noop,
  ...props
}: ColorPaletteProps): ReactElement {
  const paletteRef = useRef<HTMLDivElement | null>(null);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [persistShuffle, setPersistShuffle] = useState<boolean>(false);

  const colorPaletteAppliedEventHandler = (): void => {
    onColorPaletteDoneApplying();
  };

  useEffect(() => {
    if (!props.colorPaletteAppliedEvent) {
      return;
    }

    window.addEventListener(props.colorPaletteAppliedEvent, colorPaletteAppliedEventHandler);

    return (): void => {
      if (!props.colorPaletteAppliedEvent) {
        return;
      }

      window.removeEventListener(props.colorPaletteAppliedEvent, colorPaletteAppliedEventHandler);
    };
  }, []);

  const getSizeModifier = (): string => {
    switch (size) {
      case ColorPaletteSize.SMALL:
        return styles.small;
      case ColorPaletteSize.MEDIUM:
      default:
        return '';
    }
  };

  const getNameComponent = (): ReactElement | null => {
    if (!showNameLabel || !props.palette?.name) {
      return null;
    }

    return (
      <Text val={props.palette.name} size={TextSize.XSMALL} className={`${styles.textOverflowEllipsis} ${isSelected ? 'content-primary' : 'content-sub-text'}`} bold={isSelected} />
    );
  };

  const getOverlayComponentForShuffleAllowedPalette = (): ReactElement | null => {
    if (!props.palette?.name) {
      return null;
    }

    const showOnlyShuffleState = isMobile || !showNameOverlayOnHover;

    if (showOnlyShuffleState) {
      if (!isSelected) {
        return null;
      }

      return (
        <div className={`${styles.overlay} ${isSelected ? styles.overlayVisible : ''}`}>
          <div className={'flex-center _full-height _full-width'}>
            <motion.div animate={{opacity: isSelected ? 1 : 0}} transition={{duration: 0.3, ease: 'easeInOut'}} className={'flex-center gap-2'}>
              <Icon type={IconType.FLAT} icon="icon-shuffle" className="content-body-white" size={IconSize.SIZE_ICON_20} />
              <Text val={window.i18next.t('pmwjs_shuffle')} bold size={TextSize.XSMALL} className="content-body-white body-lg-regular" />
            </motion.div>
          </div>
        </div>
      );
    }

    const overlayVisible = !allowShuffle || isBeingApplied || isSelected;

    const showShuffleText = (isSelected && !isHovered) || persistShuffle;
    return (
      <div className={`${styles.overlay} ${overlayVisible ? styles.overlayVisible : ''}`}>
        <div className={'flex-center _full-height _full-width'}>
          <motion.div initial={false} animate={{opacity: showShuffleText ? 1 : 0}} transition={{duration: 0.3, ease: 'easeInOut'}} className={'flex-center gap-1'}>
            <Icon type={IconType.FLAT} icon="icon-shuffle" className="content-body-white" size={IconSize.SIZE_ICON_20} />
            <Text val={window.i18next.t('pmwjs_shuffle')} bold size={TextSize.XSMALL} className="content-body-white body-lg-regular" />
          </motion.div>

          {isMobile ? null : (
            <motion.div initial={false} animate={{opacity: showShuffleText ? 0 : 1}} transition={{duration: 0.3, ease: 'easeInOut'}} className={'flex-center'}>
              <Text val={props.palette.name} bold size={TextSize.XSMALL} className={`content-body-white ${styles.overlayText}`} />
            </motion.div>
          )}
        </div>
      </div>
    );
  };

  const getOverlayComponentForNonShuffleAllowedPalette = (): ReactElement | null => {
    if (!props.palette?.name) {
      return null;
    }

    if (!showNameOverlayOnHover) {
      return null;
    }

    if (isMobile) {
      return null;
    }

    return (
      <div className={styles.overlay}>
        <Text val={props.palette.name} size={TextSize.XSMALL} bold className={`content-body-white ${styles.overlayText}`} />
      </div>
    );
  };

  const getOverlayComponent = (): ReactElement | null => {
    if (showApplyingState && isBeingApplied) {
      return (
        <div className={`${styles.overlay} ${styles.overlayVisible}`}>
          <div className={styles.loadingContainer}>
            <Icon icon="icon-loader" className={`${styles.loadingIcon} content-body-white`} size={IconSize.SIZE_ICON_20} />
            <Text val={window.i18next.t('pmwjs_applying_ellipsis')} size={TextSize.SMALL} className="content-body-white body-lg-regular" />
          </div>
        </div>
      );
    }

    if (allowShuffle) {
      return getOverlayComponentForShuffleAllowedPalette();
    }

    return getOverlayComponentForNonShuffleAllowedPalette();
  };

  const getColorsToDisplay = (): string[] => {
    if (!props.palette || props.palette.colors.length === 0) {
      return DEFAULT_COLORS;
    }

    return props.palette.colors;
  };

  const isSelectionDisabled = (): boolean => {
    return disableSelection || (isSelected && !allowShuffle);
  };

  const getClassNamesForPalette = (): string => {
    const isSelectable = onClick && onClick !== noop;

    return `${styles.colorPalette} ${isSelectable ? styles.selectable : ''} ${isSelected ? styles.selected : ''} ${isSelectionDisabled() ? styles.disabledForSelection : ''}`;
  };

  const updateIsHovered = (newValue: boolean, disablePersistShuffle = false): void => {
    if (isTouchDevice()) {
      return;
    }

    if (newValue === isHovered) {
      return;
    }

    if (disablePersistShuffle) {
      setPersistShuffle(false);
    }

    setIsHovered(newValue);
  };

  const clickHandler = (): void => {
    if (disableSelection) {
      return;
    }

    if (isBeingApplied) {
      return;
    }

    if (allowShuffle || !isSelected) {
      if (allowShuffle) {
        setPersistShuffle(true);
      }

      onClick();
    }
  };

  const colorsToShow = getColorsToDisplay();
  const sizeModifier = getSizeModifier();

  return (
    <div
      className={styles.colorPaletteContainer}
      onMouseEnter={() => {
        updateIsHovered(true);
      }}
      onMouseLeave={() => {
        updateIsHovered(false, true);
      }}
    >
      <ClickableDiv ref={paletteRef} className={`${getClassNamesForPalette()} ${sizeModifier}`} onClick={clickHandler}>
        {colorsToShow.map((color) => {
          return <div key={color} className={`${styles.colorBlock} ${sizeModifier}`} style={{backgroundColor: color}} />;
        })}
        {getOverlayComponent()}
      </ClickableDiv>
      {getNameComponent()}
    </div>
  );
}
